import React, { useMemo, useCallback } from "react";
import clsx from "clsx";
import { Col, Row } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";

import { Skeleton, Button, HookFormInputGroup } from "components";
import UrlInfoIcon from "components/urlInfoIcon";
import { UserProfileFields } from "globalConstants";
import useLoader from "hooks/useLoader";
import { useGetPreAppLinkMutation } from "queries";
import { uiActions } from "store";
import { MerchantsPreAppUrlProps } from "types";
import { trimMidSentenceNormalizer, trimNormalizer } from "validators";
import {
	applyValidatorsIf,
	maxLength50,
	noSpaceAllowed,
} from "validators/validation";

import { ReactComponent as CopyIcon } from "static/images/copy_icon.svg";
import { ReactComponent as PaperPlaneIcon } from "static/images/paper-plane.svg";
import styles from "../userForm.module.scss";

const validateAlias = {
	maxLength: maxLength50,
	containsLatinLetter(value) {
		return !/[a-z]+/i.test(value)
			? "Must contain at least 1 latin letter"
			: undefined;
	},
	noSpaceAllowed,
	allowedCharacters(value) {
		return !/^[a-z0-9.\-_]*$/i.test(value)
			? "Only latin letters, digits, dots and - _ are allowed"
			: undefined;
	},
};

const MerchantsPreAppUrl = ({
	defaultValues,
	url,
	alias,
	loading,
	isActiveUser,
}: MerchantsPreAppUrlProps) => {
	const { Alias, Domain, AgentId } = UserProfileFields;

	const dispatch = useDispatch();
	const Loader = useLoader(loading);

	const { register, formState } = useFormContext();
	const { errors } = formState;

	const urlEnabled = useMemo(
		() =>
			defaultValues?.[Domain.name] &&
			alias &&
			(!errors || !errors?.alias) &&
			defaultValues?.[Alias.name] === alias &&
			isActiveUser,
		[Alias.name, Domain.name, alias, defaultValues, errors, isActiveUser],
	);

	const urlSendEnabled = urlEnabled;

	const handleSendPreAppUrl = useCallback(() => {
		dispatch(
			uiActions.showModal("SEND_PRE_APP", {
				showAgentSelect: false,
				defaultUrl: url,
				defaultAgentId: defaultValues?.[AgentId.name],
			}),
		);
	}, [dispatch, url, defaultValues, AgentId.name]);

	const { mutate: getPreAppLink } = useGetPreAppLinkMutation();

	const handleCopyPreAppUrl = useCallback(() => {
		getPreAppLink(defaultValues?.[AgentId.name]);
	}, [AgentId.name, defaultValues, getPreAppLink]);

	return (
		<>
			<hr className={`${styles.divider} mt-2`} />
			<Row className={styles.aliasBlock}>
				<Col lg={5}>
					<h3 className={styles.title}>URL for merchant's pre-app</h3>
					<div className={styles.urlContainer}>
						<Loader
							skeleton={<Skeleton variant="text" width={160} />}
							component={
								<a
									href={urlEnabled ? url : undefined}
									target="_blank"
									rel="noopener noreferrer"
									className={clsx(styles.url, "url-disabled")}
								>
									{url}
								</a>
							}
						/>
						<div className={styles.urlActions}>
							<Button
								onClick={handleSendPreAppUrl}
								disabled={!urlSendEnabled}
								icon={<PaperPlaneIcon />}
								btnIcon
								variant="secondary"
								size="sm"
							/>
							<Button
								onClick={handleCopyPreAppUrl}
								disabled={!urlSendEnabled}
								icon={<CopyIcon />}
								btnIcon
								variant="secondary"
								size="sm"
								className="mr-1"
								title="Copy Link"
							/>
							<UrlInfoIcon width={13} alt="" placement="top" />
						</div>
					</div>
					<div className={styles.aliasCol2}>
						<HookFormInputGroup
							name={Alias.name}
							register={register}
							validator={applyValidatorsIf(
								() => alias && validateAlias,
							)(Object.values(validateAlias))}
							colLabel={5}
							colInput={7}
							label={Alias.displayName}
							errors={errors}
							normalizeOnBlur={trimNormalizer}
							normalize={trimMidSentenceNormalizer}
							blurLoading={loading}
						/>
					</div>
				</Col>
			</Row>
		</>
	);
};

export default MerchantsPreAppUrl;
